import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
    ArrowLeft, 
    AlertTriangle, 
    CheckCircle,
    TrendingUp,
    TrendingDown
} from 'lucide-react';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer,
    Cell
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { getAnomalyDetail, acknowledgeAnomaly } from '../lib/api';
import { cn } from '../lib/utils';

const getSeverityStyles = (severity) => {
    switch (severity) {
        case 'critical':
            return 'severity-critical';
        case 'high':
            return 'severity-high';
        case 'medium':
            return 'severity-medium';
        default:
            return 'severity-low';
    }
};

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="bg-popover border border-primary rounded-lg p-3 shadow-lg">
                <p className="font-medium text-card-foreground">{data.segment_value}</p>
                <p className="text-sm text-muted-foreground">
                    Deviation: <span className={cn(
                        "font-mono",
                        data.deviation_percent > 0 ? "text-secondary" : "text-destructive"
                    )}>{data.deviation_percent > 0 ? '+' : ''}{data.deviation_percent}%</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                    Contribution: {data.contribution}%
                </p>
            </div>
        );
    }
    return null;
};

export const AnomalyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [anomaly, setAnomaly] = useState(null);
    const [loading, setLoading] = useState(true);
    const [acknowledging, setAcknowledging] = useState(false);

    useEffect(() => {
        loadAnomaly();
    }, [id]);

    const loadAnomaly = async () => {
        try {
            setLoading(true);
            const response = await getAnomalyDetail(id);
            setAnomaly(response.data);
        } catch (error) {
            console.error('Failed to load anomaly:', error);
            toast.error('Failed to load anomaly details');
        } finally {
            setLoading(false);
        }
    };

    const handleAcknowledge = async () => {
        try {
            setAcknowledging(true);
            await acknowledgeAnomaly(id);
            setAnomaly(prev => ({ ...prev, acknowledged: 1 }));
            toast.success('Anomaly acknowledged');
        } catch (error) {
            console.error('Failed to acknowledge anomaly:', error);
            toast.error('Failed to acknowledge anomaly');
        } finally {
            setAcknowledging(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-64" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    if (!anomaly) {
        return (
            <div className="text-center py-12">
                <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Anomaly not found</p>
                <Button variant="outline" onClick={() => navigate('/anomalies')} className="mt-4">
                    Back to Anomalies
                </Button>
            </div>
        );
    }

    const deviationPercent = ((anomaly.value - anomaly.expected_value) / anomaly.expected_value * 100).toFixed(1);
    const isPositive = parseFloat(deviationPercent) > 0;

    const driverData = anomaly.drivers?.map(d => ({
        ...d,
        name: d.segment_value
    })) || [];

    return (
        <div className="space-y-6 animate-fade-in" data-testid="anomaly-detail">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => navigate('/anomalies')}
                    data-testid="back-btn"
                >
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex-1">
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold text-card-foreground">{anomaly.kpi_name}</h1>
                        <Badge 
                            variant="outline" 
                            className={cn("text-sm border", getSeverityStyles(anomaly.severity))}
                        >
                            <span className="capitalize">{anomaly.severity}</span>
                        </Badge>
                        {anomaly.acknowledged ? (
                            <Badge variant="outline" className="text-sm border-secondary text-secondary">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Acknowledged
                            </Badge>
                        ) : null}
                    </div>
                    <p className="text-muted-foreground mt-1">
                        Detected on {format(new Date(anomaly.date), 'MMMM d, yyyy')}
                    </p>
                </div>
                {!anomaly.acknowledged && (
                    <Button 
                        onClick={handleAcknowledge}
                        disabled={acknowledging}
                        className="btn-glow"
                        data-testid="acknowledge-btn"
                    >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Acknowledge
                    </Button>
                )}
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                        <p className="label-text">Actual Value</p>
                        <p className="text-3xl font-mono font-bold text-card-foreground mt-2">
                            {anomaly.value.toLocaleString()}
                        </p>
                    </CardContent>
                </Card>
                <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                        <p className="label-text">Expected Value</p>
                        <p className="text-3xl font-mono font-bold text-muted-foreground mt-2">
                            {anomaly.expected_value.toLocaleString()}
                        </p>
                    </CardContent>
                </Card>
                <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                        <p className="label-text">Deviation</p>
                        <div className="flex items-center gap-2 mt-2">
                            {isPositive ? (
                                <TrendingUp className="h-6 w-6 text-secondary" />
                            ) : (
                                <TrendingDown className="h-6 w-6 text-destructive" />
                            )}
                            <p className={cn(
                                "text-3xl font-mono font-bold",
                                isPositive ? "text-secondary" : "text-destructive"
                            )}>
                                {isPositive ? '+' : ''}{deviationPercent}%
                            </p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                        <p className="label-text">Z-Score</p>
                        <p className={cn(
                            "text-3xl font-mono font-bold mt-2",
                            Math.abs(anomaly.z_score) >= 3 ? "text-destructive" : "text-orange-400"
                        )}>
                            {anomaly.z_score.toFixed(2)}σ
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Driver Analysis */}
            {driverData.length > 0 && (
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="text-lg font-semibold text-card-foreground">
                            Driver Analysis
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                            Top contributing segments to this anomaly
                        </p>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[400px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart 
                                    data={driverData} 
                                    layout="vertical"
                                    margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
                                >
                                    <CartesianGrid 
                                        strokeDasharray="3 3" 
                                        stroke="hsl(210, 20%, 18%)"
                                        horizontal={true}
                                        vertical={false}
                                    />
                                    <XAxis 
                                        type="number"
                                        tick={{ fontSize: 11, fill: 'hsl(220, 10%, 55%)' }}
                                        tickFormatter={(v) => `${v}%`}
                                        domain={['dataMin - 10', 'dataMax + 10']}
                                    />
                                    <YAxis 
                                        type="category"
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: 'hsl(220, 10%, 55%)' }}
                                        width={90}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar 
                                        dataKey="deviation_percent"
                                        radius={[0, 4, 4, 0]}
                                    >
                                        {driverData.map((entry, index) => (
                                            <Cell 
                                                key={index}
                                                fill={entry.deviation_percent > 0 
                                                    ? 'hsl(174, 38%, 48%)' 
                                                    : 'hsl(348, 100%, 50%)'
                                                }
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Driver Table */}
                        <div className="mt-6 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border">
                                        <th className="text-left py-3 px-4 label-text">Segment</th>
                                        <th className="text-left py-3 px-4 label-text">Type</th>
                                        <th className="text-right py-3 px-4 label-text">Value</th>
                                        <th className="text-right py-3 px-4 label-text">Expected</th>
                                        <th className="text-right py-3 px-4 label-text">Deviation</th>
                                        <th className="text-right py-3 px-4 label-text">Contribution</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {driverData.map((driver, idx) => (
                                        <tr key={idx} className="border-b border-border hover:bg-white/5">
                                            <td className="py-3 px-4 font-medium text-card-foreground">
                                                {driver.segment_value}
                                            </td>
                                            <td className="py-3 px-4 text-muted-foreground capitalize">
                                                {driver.segment_type}
                                            </td>
                                            <td className="py-3 px-4 text-right font-mono text-card-foreground">
                                                {driver.value.toLocaleString()}
                                            </td>
                                            <td className="py-3 px-4 text-right font-mono text-muted-foreground">
                                                {driver.expected_value.toLocaleString()}
                                            </td>
                                            <td className={cn(
                                                "py-3 px-4 text-right font-mono",
                                                driver.deviation_percent > 0 ? "text-secondary" : "text-destructive"
                                            )}>
                                                {driver.deviation_percent > 0 ? '+' : ''}{driver.deviation_percent}%
                                            </td>
                                            <td className="py-3 px-4 text-right font-mono text-muted-foreground">
                                                {driver.contribution}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default AnomalyDetail;
